import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import {
  Row,
  Col,
  Badge,
  Card,
  CardHeader,
  ListGroup,
  ListGroupItem,
  Button,
  FormCheckbox,
  FormInput,
  InputGroup,
  InputGroupAddon,
  Tooltip,
} from 'shards-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { success, error, warning } from 'react-toastify-redux';
import { confirmAlert } from 'react-confirm-alert';
import {
  getOrder,
  getLastOrder,
  getPaymentLink,
  getOrderLog,
  createOrderLog,
  deleteOrderLog,
  createOrder,
  updateOrder,
  updateOrderStatus,
  sendInvoice,
  sendDeliveryScheduleLink,
  sendPaymentLink,
  downloadInvoice,
  downloadInvoiceVersion,
  downloadDO,
  sendDO,
  OrderActionTypes,
} from '../../store/actions/orders';
import {
  createPaymentIntent,
  cnacelPayment,
} from '../../store/actions/payments';
import { getOrderByPaymentToken } from '../../store/actions/misc';
import { getOrderReport } from '../../store/actions/orderReports';
import { UIActionTypes } from '../../store/actions/ui';
import { getProducts } from '../../store/actions/products';
import { applyPromo } from '../../store/actions/promotions';
import { getUsers } from '../../store/actions/users';
import {
  getAvailableDeliverySlots,
  getAvailableDeliverySlotByDate,
  findScheduleCapacityConflicts,
} from '../../store/actions/deliverySlots';
import { MainStage } from '../../components/layout';
import {
  FormCard,
  FormGroup,
  FormInputGroup,
  DialogButton,
  Dialog,
  IconButton,
  OrderLog,
  MaterialIcon,
  StripePaymentInfo,
  AtomePaymentInfo,
  HitPayPaymentInfo,
  PageLoader,
} from '../../components/common';
import ProductModal from './ProductModal';
import CustomProductModal from './CustomProductModal';
import PromoModal from './PromoModal';
import BundleModal from './BundleModal';
import AddBundleModal from './AddBundleModal';
import ScheduleModal from './ScheduleModal';
import PreviewModal from './PreviewModal';
import PaymentModal from './PaymentModal';
import { calculateTax } from '../../utils';
import { hasAccess, getAdmin } from '../../utils/auth';
import {
  DeliveryOptions,
  OrderStatus,
  PromosColor,
  ShippingCountries,
  PromoTypes,
  PromoBundleGetTypes,
  StatusColors,
  ProductStatusColor,
  ProductStatus,
  ProductLifecycleColor,
  PaymentTypes,
  PaymentStatus,
  PaymentStatusColors,
  ProductLifecycle,
} from '../../constants';

const _ = require('lodash');

const ProductTable = styled.table`
  th {
    border-top: none;
  }
  td {
    vertical-align: middle;
    img {
      max-width: 100px;
      margin-right: 10px;
    }
  }
`;
const SummaryTable = styled.table`
  min-width: 40%;
  float: right;
  td {
    font-weight: 500;
    padding: 3px;
  }
`;

const CommonList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    display: flex;
    padding: 7px 0;
    button {
      margin-left: auto;
    }
  }
`;

const RetiredList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  li {
    padding: 0.5rem 0;
    border-bottom: 1px solid #e9ecef;
  }
  li:first-child {
    border-top: 1px solid #e9ecef;
  }
  .retired-meta {
    margin-top: 0.25rem;
  }
`;

const ActivityList = styled.ul`
  list-style: none;
  padding: 0;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  li {
    display: flex;
    padding: 0;
  }
`;

// an order line's promo chips: shards' outline badge supplies the border in
// the promo's own colour, the same one the Redeems panel uses, and this makes
// the inside white with black text so the line stays easy to read
const LinePromoBadge = styled(Badge)`
  background-color: #fff;
  color: #212529;
  font-weight: 500;
  margin-top: 0.4rem;
`;

const ProductTd = styled.td`
  display: flex;
  align-items: center;
`;

/**
 * Lifecycles that should not reach a new order. The product picker already
 * hides them (`ProductModal`), but a line can still arrive from a bundle, from
 * an order saved before the product was retired, or from a product retired
 * while the order sat open -- so the save path warns rather than blocks.
 */
const RETIRED_LIFECYCLES = [
  ProductLifecycle.INACTIVE,
  ProductLifecycle.DISCONTINUED,
];

const ProductForm = () => {
  const { control, handleSubmit, errors, getValues, setValue } = useForm();
  const { id } = useParams();
  const { search } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoading = useSelector(({ ui }) => ui.isLoading);
  const pageLoading = useSelector(({ ui }) => ui.pageLoading);
  const productsData = useSelector(({ products: { products } }) => products);
  const orderData = useSelector(({ orders: { order } }) => order);
  const paymentLinkData = useSelector(
    ({ orders: { paymentLink } }) => paymentLink
  );
  const lastOrderData = useSelector(({ orders: { lastOrder } }) => lastOrder);
  const { items } = useSelector(({ users: { users } }) => users);
  const deliverySlotsData = useSelector(
    ({ deliverySlots: { availableDeliverySlots } }) => availableDeliverySlots
  );
  const orderReportData = useSelector(
    ({ orderReports: { orderReport } }) => orderReport
  );
  const orderLogData = useSelector(({ orders: { orderLog } }) => orderLog);
  const paymentTokenData = useSelector(
    ({ misc: { paymentToken } }) => paymentToken
  );
  const paymentLinkRef = useRef(null);
  const [orderState, setOrderState] = useState(null);
  const [orderProducts, setOrderProducts] = useState([]);
  const [orderPromos, setOrderPromos] = useState([]);
  const [linePromos, setLinePromos] = useState({});
  const [deliverySchedules, setDeliverySchedules] = useState([]);
  const [deliveryScheduleItems, setDeliveryScheduleItems] = useState([]);
  const [itemView, setItemView] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [payments, setPayments] = useState([]);
  const [orderReport, setOrderReport] = useState(null);
  const [orderLog, setOrderLog] = useState(null);
  const [orderComment, setOrderComment] = useState('');
  const [availableDeliverySlots, setAvailableDeliverySlots] = useState([]);
  const [orderActions, setOrderActions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [openProductModal, setOpenProductModal] = useState(false);
  const [openCustomProductModal, setOpenCustomProductModal] = useState(false);
  const [openPromoModal, setOpenPromoModal] = useState(false);
  const [openBundleModal, setOpenBundleModal] = useState(false);
  const [openAddBundleModal, setOpenAddBundleModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [bundlePromotion, setBundlePromotion] = useState(null);
  const [showGuestEmail, setShowGuestEmail] = useState(false);
  const [copyAddress, setCopyAddress] = useState(false);
  const [tooltipClipboard, setTooltipClipboard] = useState(false);
  const [paymentModalItemIndex, setPaymentModalItemIndex] = useState(-1);
  const canCreate = hasAccess('orders', 'create');
  const canUpdate = hasAccess('orders', 'update');
  const currentAdmin = getAdmin();

  const orderStatusOptions = [];
  Object.keys(OrderStatus).forEach((key) => {
    const status = OrderStatus[key];
    if (status !== OrderStatus.CANCELLED && status !== OrderStatus.REFUNDED)
      orderStatusOptions.push({ value: status, label: status });
  });

  const shippingCountriesOptions = Object.keys(ShippingCountries).map((key) => {
    return {
      value: ShippingCountries[key],
      label: ShippingCountries[key],
    };
  });

  // Address lookup is explicit: it runs only when the user clicks "Look up"
  // beside a postal code. Nothing on this form fills the address on its own, so
  // a page load, an order/customer prefill or "same as billing" can no longer
  // overwrite an address that was typed by hand. The token lets an in-flight
  // lookup be discarded if the user starts editing the address before it lands.
  const lookupTokenRef = useRef({ billing: 0, shipping: 0 });
  // the schedules as the order was loaded, so a save can tell which ones
  // actually moved. deliverySchedules holds the very objects orderData does,
  // so orderState cannot serve as the before-picture.
  const savedSchedulesRef = useRef([]);
  const [lookingUpAddress, setLookingUpAddress] = useState({
    billing: false,
    shipping: false,
  });

  const cancelAddressLookup = (formName) => {
    lookupTokenRef.current[formName] += 1;
  };

  const handleAddressLookup = async (formName) => {
    const postal = (getValues()[`${formName}.postalCode`] || '').trim();

    if (!/^\d{6}$/.test(postal)) {
      dispatch(warning('Enter a 6-digit postal code first'));
      return;
    }

    cancelAddressLookup(formName);
    const token = lookupTokenRef.current[formName];
    setLookingUpAddress((prev) => ({ ...prev, [formName]: true }));

    try {
      const res = await fetch(
        `https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${postal}&returnGeom=N&getAddrDetails=Y`
      );
      const data = await res.json();

      // the user started editing the address while this was in flight
      if (token !== lookupTokenRef.current[formName]) return;

      const result = data.found ? data.results[0] : null;
      // OneMap returns the literal string 'NIL' for a missing building name
      const address = result
        ? [result.BLK_NO, result.ROAD_NAME, result.BUILDING]
            .filter((part) => part && part !== 'NIL')
            .join(' ')
        : '';

      if (!address) {
        dispatch(warning(`No address found for postal code ${postal}`));
        return;
      }

      setValue(`${formName}.address`, address);
      // OneMap only resolves Singapore postal codes, so a hit settles the country
      setValue(`${formName}.country`, ShippingCountries.SINGAPORE);
    } catch (e) {
      dispatch(error('Could not reach the postal code lookup service'));
    } finally {
      setLookingUpAddress((prev) => ({ ...prev, [formName]: false }));
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getOrder(id));
      dispatch(getOrderLog(id));
      dispatch(getOrderReport(id));
    } else {
      const newOrder = {
        deliveryOption: DeliveryOptions.ALL,
        shippingFee: 0,
        referral: {},
        billing: {},
        shipping: {},
      };
      setOrderState(newOrder);
    }
    dispatch(getProducts());
    // clear payment link in reducer
    dispatch({
      type: OrderActionTypes.GET_PAYMENT_LINK,
      paymentLink: '',
    });
  }, [id]);

  useEffect(() => {
    if (deliverySlotsData.items.length)
      setAvailableDeliverySlots(deliverySlotsData.items);
    else dispatch(getAvailableDeliverySlots());
  }, [deliverySlotsData]);

  useEffect(() => {
    if (orderReportData && id && orderReportData.id === id) {
      setOrderReport(orderReportData);
    }
  }, [orderReportData]);

  useEffect(() => {
    if (paymentTokenData && id && paymentTokenData.id === id) {
      dispatch(
        createPaymentIntent(paymentTokenData.token, PaymentTypes.HITPAY)
      );
    }
  }, [paymentTokenData]);

  useEffect(() => {
    if (copyAddress) {
      const data = getValues();
      setValue('shipping.address', data['billing.address']);
      setValue('shipping.company', data['billing.company']);
      setValue('shipping.country', data['billing.country']);
      setValue('shipping.postalCode', data['billing.postalCode']);
      setValue('shipping.email', data['billing.email']);
      setValue('shipping.firstName', data['billing.firstName']);
      setValue('shipping.lastName', data['billing.lastName']);
      setValue('shipping.contactNo', data['billing.contactNo']);
    }
  }, [copyAddress]);

  useEffect(() => {
    if (orderLogData) setOrderLog(orderLogData);
  }, [orderLogData]);

  useEffect(() => {
    const options = [{ value: 'Guest', label: 'Guest' }];
    if (items.length) {
      const [users] = items;
      users.forEach((i) => {
        options.push({ value: i.id, label: `${i.firstName} ${i.lastName}` });
      });
    }
    setCustomerOptions(options);
  }, [items]);

  useEffect(() => {
    if (orderData && id && orderData.id === id) {
      const paymentStatus = new URLSearchParams(search).get('status');
      const paymentId = new URLSearchParams(search).get('reference');
      if (paymentStatus && paymentId) {
        switch (paymentStatus) {
          case 'canceled':
          case 'cancelled':
            const paymentIndex = orderData.payments
              ? orderData.payments.findIndex(
                  (p) =>
                    p[p.type].id === paymentId &&
                    p.status === PaymentStatus.PENDING
                )
              : null;
            if (paymentIndex !== -1) {
              dispatch(
                cnacelPayment(
                  id,
                  paymentId,
                  orderData.payments[paymentIndex].type
                )
              );
              orderData.payments.splice(paymentIndex, 1);
            }
            break;
          case 'completed':
            dispatch(success('Payment has been successfully made'));
            break;
          case 'failed':
            dispatch(error('Payment failed, please try again'));
            break;
          default:
            break;
        }
        history.push(`/orders/edit/${id}`);
      }

      orderData.orderedOn = moment.unix(orderData.orderedOn.seconds).toDate();
      setOrderState(orderData);

      const actions = [];
      if (
        orderData.status === OrderStatus.PENDING_PAYMENT ||
        orderData.status === OrderStatus.PROCESSING ||
        orderData.status === OrderStatus.COMPLETED
      ) {
        actions.push(
          { text: 'Send schedule link', showDialog: true },
          { text: 'Send updated invoice', showDialog: true },
          { text: 'Print invoice', showDialog: true }
        );
      }

      if (orderData.status === OrderStatus.PENDING_PAYMENT) {
        dispatch(getPaymentLink(id));
        actions.push({ text: 'Send payment link', showDialog: true });
        // actions.push({ text: 'HitPay checkout', showDialog: true });
      }

      if (
        orderData.status !== OrderStatus.REFUNDED &&
        orderData.status !== OrderStatus.CANCELLED
      ) {
        if (orderData.status !== OrderStatus.PENDING_PAYMENT)
          actions.push({ text: 'Refund', showDialog: true });
        actions.push({ text: 'Cancel order', showDialog: true });
      }

      setOrderActions(actions);

      if (orderData.products) {
        orderData.products.forEach((p) => {
          if (typeof p.shippable === 'undefined') p.shippable = true;
          if (p.statusDate)
            p.statusDate = moment.unix(p.statusDate.seconds).toDate();
        });
        setOrderProducts(orderData.products);
      }
      if (orderData.promos) {
        setOrderPromos(orderData.promos);
        // `refs` is keyed by position in the saved products array, so resolve
        // it to each line's uuid now — once the admin adds or removes a line
        // those positions no longer mean anything.
        const resolved = {};
        orderData.promos.forEach((promo) => {
          Object.keys(promo.refs || {}).forEach((index) => {
            const product = (orderData.products || [])[Number(index)];
            if (!product || !product.uuid) return;
            resolved[product.uuid] = resolved[product.uuid] || [];
            // only which code touched the line, never `ref.amount`: older
            // orders stored the unit value rather than the line total, an
            // `entire`-scoped promo contributes nothing to refs at all, and an
            // edit can leave the figure behind. the attribution is sound, the
            // money is not.
            resolved[product.uuid].push({
              code: promo.code,
              type: promo.type,
            });
          });
        });
        setLinePromos(resolved);
      }
      if (orderData.schedules) {
        orderData.schedules.map((s) => {
          s.date = moment.unix(s.date.seconds).toDate();
          s.printable = true;
          if (!s.uuid) setItemView(false);
          return s;
        });
        savedSchedulesRef.current = orderData.schedules.map((s) => ({
          uuid: s.uuid,
          createdUnix: s.createdUnix,
          id: s.id,
          time: s.time,
        }));
        setDeliverySchedules(orderData.schedules);
      }
      if (orderData.payments) {
        orderData.payments.map((p) => {
          if (p.paidOn) p.paidOn = moment.unix(p.paidOn.seconds).toDate();
          return p;
        });
        setPayments(orderData.payments);
      }
    }
    // eslint-disable-next-line
  }, [orderData]);

  useEffect(() => {
    const scheduledProducts = [];
    deliverySchedules.forEach((s) => {
      s.products.forEach((p) => {
        let qtyCount = p.qty;
        let allocatedQtyCount = p.allocatedQty;
        while (qtyCount > 0) {
          scheduledProducts.push({
            date: s.date,
            time: s.time,
            status: s.status,
            allocated: allocatedQtyCount > 0,
            ...p,
          });
          qtyCount -= 1;
          allocatedQtyCount -= 1;
        }
      });
    });

    const scheduleItems = [];
    orderProducts
      .filter((p) => p.shippable)
      .forEach((p) => {
        let qtyCount = p.qty;
        while (qtyCount > 0) {
          const spIndex = scheduledProducts.findIndex((s) => s.uuid === p.uuid);
          if (spIndex !== -1) {
            const sp = scheduledProducts[spIndex];
            if (sp) scheduledProducts.splice(spIndex, 1);
            scheduleItems.push({
              ...sp,
            });
          } else {
            scheduleItems.push({
              name: p.name,
              variant: p.variant,
            });
          }
          qtyCount -= 1;
        }
      });
    setDeliveryScheduleItems(_.orderBy(scheduleItems, 'date'));
  }, [deliverySchedules, orderProducts]);

  useEffect(() => {
    if (lastOrderData !== null) {
      const { billing, shipping } = lastOrderData;
      setValue('billing.firstName', billing.firstName);
      setValue('billing.lastName', billing.lastName);
      setValue('billing.email', billing.email);
      setValue('billing.contactNo', billing.contactNo);
      setValue('billing.company', billing.company);
      setValue('billing.address', billing.address);
      setValue('billing.country', billing.country);
      setValue('billing.postalCode', billing.postalCode);

      setValue('shipping.firstName', shipping.firstName);
      setValue('shipping.lastName', shipping.lastName);
      setValue('shipping.email', shipping.email);
      setValue('shipping.contactNo', shipping.contactNo);
      setValue('shipping.company', shipping.company);
      setValue('shipping.address', shipping.address);
      setValue('shipping.country', shipping.country);
      setValue('shipping.postalCode', shipping.postalCode);
    }
  }, [lastOrderData]);

  /**
   * The customer the order is for: the saved one when editing, otherwise
   * whatever the customer select holds.
   *
   * Promo validation is done per user, so the backend refuses an order with no
   * `userId` — and its message, "User not found", reads as if the customer was
   * deleted rather than never chosen. On a new order the field is empty until
   * the admin picks someone, and its `required` rule only fires on submit.
   * @returns {string} user id, or '' when no customer is selected
   */
  const getSelectedUserId = () => {
    if (id) return (orderState && orderState.userId) || '';
    const { customer } = getValues();
    return (customer && customer.value) || '';
  };

  /**
   * Build the order payload the promo endpoints expect.
   * @param {Array} [productsOverride] lines to price instead of `orderProducts`
   *   — needed when a caller has just computed new lines but React has not
   *   committed the state update yet.
   */
  const computeOrder = (productsOverride) => {
    const data = getValues();
    // order object
    const order = {
      orderedOn: data.orderedOn,
      userId: id ? orderState.userId : data.customer.value,
      shippingFee: parseFloat(data.shippingFee),
      shipping: {
        country: data['shipping.country'],
      },
      products: _.cloneDeep(productsOverride || orderProducts),
    };
    // calculate order price
    const totalPrice = order.products.reduce(
      (total, p) => total + (p.subtotal || 0),
      0
    );
    order.totalPrice = totalPrice;
    return order;
  };

  /**
   * Order lines whose catalogue product has been retired.
   *
   * Lifecycle lives on the product, never on the order line, so it has to be
   * resolved against the catalogue on every save -- that is what catches
   * lines a bundle added and lines that predate the retirement. Ad-hoc lines
   * (no `id`) and lines whose product no longer exists are skipped: there is
   * no lifecycle to read.
   * @returns {Array} `[{ name, variant, qty, lifecycle, bundleCode }]`
   */
  const getRetiredLines = () =>
    orderProducts.reduce((acc, p) => {
      if (!p.id) return acc;
      const product = productsData.items.find((i) => i.id === p.id);
      if (!product) return acc;
      // absent on every document predating the lifecycle field, and that
      // means Active
      const lifecycle = product.lifecycle || ProductLifecycle.ACTIVE;
      if (!RETIRED_LIFECYCLES.includes(lifecycle)) return acc;
      acc.push({
        name: p.name,
        variant: p.variant,
        qty: p.qty,
        lifecycle,
        bundleCode: p.bundleCode,
      });
      return acc;
    }, []);

  /**
   * Ask before saving an order that still carries a retired product.
   * @param {Array} lines from `getRetiredLines`
   * @param {Function} onConfirm run when the admin chooses to save anyway
   */
  const confirmRetiredLines = (lines, onConfirm) => {
    confirmAlert({
      customUI: (res) => (
        <Dialog>
          <h4>
            {lines.length === 1
              ? 'This order has a retired product'
              : 'This order has retired products'}
          </h4>
          <p className="mb-2">
            The following {lines.length === 1 ? 'item is' : 'items are'} no
            longer meant to be sold. Save the order anyway?
          </p>
          <RetiredList>
            {lines.map((line) => (
              <li key={`${line.name}-${line.variant}-${line.bundleCode || ''}`}>
                <div>
                  {line.name} &mdash; {line.variant} &times; {line.qty}
                </div>
                <div className="retired-meta">
                  <Badge
                    theme={
                      ProductLifecycleColor[
                        line.lifecycle.replaceAll(' ', '_').toUpperCase()
                      ] || 'secondary'
                    }
                  >
                    {line.lifecycle}
                  </Badge>
                  {line.bundleCode && (
                    <small className="text-muted ml-2">
                      from bundle {line.bundleCode}
                    </small>
                  )}
                </div>
              </li>
            ))}
          </RetiredList>
          <div className="d-flex justify-content-end">
            <Button
              className="mr-2"
              outline
              theme="secondary"
              type="button"
              onClick={res.onClose}
            >
              Cancel
            </Button>
            <Button
              outline
              theme="danger"
              type="button"
              onClick={() => {
                res.onClose();
                onConfirm();
              }}
            >
              Save anyway
            </Button>
          </div>
        </Dialog>
      ),
    });
  };

  const onSubmit = async (data) => {
    if (id && !canUpdate) {
      dispatch(warning('You do not have permission to update order'));
      return;
    }
    if (!id && !canCreate) {
      dispatch(warning('You do not have permission to create order'));
      return;
    }

    if (!orderProducts.length) {
      dispatch(warning('Please add at least one product'));
      return;
    }

    // Slots are re-checked again here, not just when a schedule is added: the
    // modal validates against form state and the slot can fill between adding
    // a schedule and saving the order. Nothing downstream enforces capacity —
    // the order trigger appends to the slot after the order is already written.
    // Only schedules that moved or were added are checked: an untouched one
    // already holds its slot, so editing anything else on the order must not
    // be blocked by a slot that has since filled up.
    const capacityConflicts = await findScheduleCapacityConflicts(
      deliverySchedules,
      orderState && orderState.refNum,
      savedSchedulesRef.current
    );
    if (capacityConflicts.length) {
      capacityConflicts.forEach((c) => {
        if (c.missing) {
          dispatch(
            warning(
              `${c.time} is no longer offered on that date — remove that schedule and pick another slot.`
            )
          );
          return;
        }
        const free = Math.max(c.capacity - c.others, 0);
        dispatch(
          warning(
            `${moment(c.date.seconds * 1000).format('DD MMM YYYY')} ${
              c.time
            } only has ${free} of ${c.capacity} left but this order needs ${
              c.mine
            }. Order not saved.`
          )
        );
      });
      return;
    }

    // order object
    const order = {
      ...orderState,
      remarks: data.remarks,
      status: data.status,
      orderedOn: data.orderedOn,
      shippingFee: parseFloat(data.shippingFee),
      billing: {
        ...data.billing,
      },
      shipping: {
        ...data.shipping,
      },
      payments: _.cloneDeep(payments),
      products: _.cloneDeep(orderProducts),
      schedules: _.cloneDeep(deliverySchedules),
      currency: orderProducts[0].currency,
    };

    delete order.id;
    delete order.user;

    const commit = () => {
      if (!id) {
        order.userId = data.customer.value;
        order.guestEmail = data.guestEmail;
        order.guestFirstName = data.guestFirstName;
        order.guestLastName = data.guestLastName;
        order.referral = data.referral;
        const promoCodes = orderPromos.map((op) => {
          return op.code;
        });
        dispatch(createOrder(order, promoCodes));
      } else {
        order.promos = orderPromos;
        order.userId = orderState.userId;
        dispatch(updateOrder(id, order));
      }
    };

    // last gate before the order is written: the picker cannot catch a line
    // that arrived with a bundle or was already on the order
    const retiredLines = getRetiredLines();
    if (retiredLines.length) {
      confirmRetiredLines(retiredLines, commit);
      return;
    }

    commit();
  };

  const handleOrderAction = (index, action) => {
    switch (action.text) {
      case 'Cancel order':
        dispatch(updateOrderStatus(id, OrderStatus.CANCELLED));
        break;
      case 'Refund':
        dispatch(updateOrderStatus(id, OrderStatus.REFUNDED));
        break;
      case 'Send updated invoice':
        dispatch(sendInvoice(id, true));
        break;
      case 'Send schedule link':
        dispatch(sendDeliveryScheduleLink(id));
        break;
      case 'Print invoice':
        dispatch(downloadInvoice(id));
        break;
      case 'Send payment link':
        dispatch(sendPaymentLink(id, true));
        break;
      case 'HitPay checkout':
        dispatch(getOrderByPaymentToken(id));
        break;
      default:
    }
  };

  /**
   * Validate a promo code against the current cart and attach it to the order.
   * @param {string} code
   * @param {Array} [productsOverride] validate against these lines instead of
   *   `orderProducts`, for callers that add lines and apply in one go
   * @returns {Promise<boolean>} whether the code was attached
   */
  const handleApplyOrderPromo = async (code, productsOverride) => {
    if (!getSelectedUserId()) {
      dispatch(warning("Missing customer, can't apply promo"));
      return false;
    }
    const order = computeOrder(productsOverride);
    dispatch({ type: UIActionTypes.START_LOADING });
    const promoCodes = orderPromos.map((op) => {
      return op.code;
    });
    const res = await applyPromo(code, order, promoCodes);
    dispatch({ type: UIActionTypes.STOP_LOADING });
    const { promotion, message } = res;
    if (promotion) {
      if (orderPromos.findIndex((p) => p.code === code) !== -1) {
        dispatch(warning('Promo code already applied in this order'));
        return false;
      }
      if (!promotion.usageLimits.canStack && orderPromos.length) {
        dispatch(warning('This code cannot be stacked'));
        return false;
      }

      setOrderPromos((prev) => [
        ...prev,
        // drop `refs`: the promo document can carry leftovers from an earlier
        // order, and this order's shares are only computed server-side on save
        _.omit(promotion, ['refs']),
      ]);
      setOpenPromoModal(false);
      if (
        promotion.type === PromoTypes.BUNDLE &&
        promotion.bundle.gets &&
        promotion.bundle.gets.find(
          (g) =>
            g.type === PromoBundleGetTypes.COLLECTIONS ||
            g.type === PromoBundleGetTypes.PRODUCTS
        )
      ) {
        setOpenBundleModal(true);
        setBundlePromotion(promotion);
      }
      return true;
    }
    dispatch(warning(message));
    return false;
  };
  /**
   * The promo badges to show against one order line.
   * Only ever populated from a saved order: a line added or a code applied in
   * this session is not attributed until the server prices it on save.
   * @param {Object} product
   * @returns {Array} `[{ code, type }]`
   */
  const productPromos = (product) =>
    ((product.uuid && linePromos[product.uuid]) || []).filter(
      // the "<code> bundle" chip beside it already names this promo
      (promo) => promo.code !== product.bundleCode
    );

  /**
   * Drop every line badge belonging to one promo code.
   * @param {string} promoCode
   * @returns {Object} the next `linePromos` map
   */
  const dropLinePromo = (promoCode) =>
    Object.entries(linePromos).reduce((acc, [uuid, entries]) => {
      const kept = entries.filter((e) => e.code !== promoCode);
      if (kept.length) acc[uuid] = kept;
      return acc;
    }, {});

  /**
   * Strip the markers that tie a line to an "add bundle" application, leaving
   * an ordinary order line behind.
   * @param {Object} product
   */
  const clearBundleTags = (product) =>
    _.omit(product, ['bundleGroupId', 'bundleCode', 'bundleMinQty']);

  const handleRemovePromo = (promoCode, index) => {
    setOrderProducts(
      orderProducts
        .filter((value) => value.promoCode !== promoCode)
        // lines populated by the "add bundle" shortcut stay in the order as
        // ordinary products once the promo they qualified for is gone
        .map((value) =>
          value.bundleCode === promoCode ? clearBundleTags(value) : value
        )
    );
    setOrderPromos(orderPromos.filter((_value, i) => i !== index));
    setLinePromos(dropLinePromo(promoCode));
  };

  /**
   * Populate the order with a bundle's required items and apply its promo.
   *
   * The promo has to be validated against a cart that already holds the items,
   * so the new lines are passed to `handleApplyOrderPromo` explicitly rather
   * than read back from state, which React has not committed yet. If the code
   * is refused the lines are rolled back — they only exist to satisfy it.
   * @param {Array} lines order lines built by the bundle picker
   * @param {string} code the bundle promo code
   */
  const handleConfirmAddBundle = async (lines, code) => {
    setOpenAddBundleModal(false);
    if (!lines || !lines.length) return;

    if (orderPromos.find((p) => p.code === code)) {
      dispatch(warning('Promo code already applied in this order'));
      return;
    }

    const nextProducts = [...orderProducts, ...lines];
    setOrderProducts(nextProducts);

    const applied = await handleApplyOrderPromo(code, nextProducts);
    if (!applied) {
      // roll back by uuid rather than restoring the old array, so anything
      // the admin changed while the code was being checked is not lost
      const addedUuids = lines.map((line) => line.uuid);
      setOrderProducts((prev) =>
        prev.filter((product) => !addedUuids.includes(product.uuid))
      );
    }
  };

  const handleConfirmBundle = (redeemProducts) => {
    if (!redeemProducts) return;
    redeemProducts.forEach((product) => {
      setOrderProducts((prev) => [
        ...prev,
        {
          ...product,
        },
      ]);
    });
    setOpenBundleModal(false);
  };

  const handleAddProduct = async (product) => {
    setOrderProducts((prev) => [
      ...prev,
      {
        ...product,
      },
    ]);
    setOpenProductModal(false);
  };

  /**
   * What the delete confirmation should warn about for a given line.
   * Removing a bundle item takes its promo — and any free items that promo
   * redeemed — with it, which is worth saying before the admin commits.
   * @param {Object} product
   * @returns {string} dialog body, '' for an ordinary line
   */
  const removeProductMessage = (product) => {
    if (!product.bundleCode) return '';
    const hasRedeemed = orderProducts.some(
      (p) => p.promoCode === product.bundleCode
    );
    return hasRedeemed
      ? `The ${product.bundleCode} bundle promo and its free items will be removed from this order.`
      : `The ${product.bundleCode} bundle promo will be removed from this order.`;
  };

  const handleRemoveProduct = (index) => {
    const removeProduct = orderProducts[index];
    const isScheduled = (product) =>
      deliverySchedules.some((schedule) =>
        schedule.products.find((p) => p.uuid === product.uuid)
      );

    if (isScheduled(removeProduct)) {
      dispatch(
        warning(
          `Please remove delivery schedule before you can remove the product`
        )
      );
      return;
    }

    // A bundle line only qualifies for its promo while the whole bundle is in
    // the order, so removing one drops the promo and the free items it
    // redeemed. The bundle's other items stay, at their normal price.
    if (removeProduct.bundleCode) {
      const { bundleCode, bundleGroupId } = removeProduct;
      const redeemed = orderProducts.filter((p) => p.promoCode === bundleCode);
      if (redeemed.some(isScheduled)) {
        dispatch(
          warning(
            `Please remove the delivery schedule for the ${bundleCode} free items first`
          )
        );
        return;
      }

      setOrderProducts(
        orderProducts
          .filter((value, i) => i !== index)
          .filter((value) => value.promoCode !== bundleCode)
          .map((value) =>
            value.bundleGroupId === bundleGroupId
              ? clearBundleTags(value)
              : value
          )
      );
      setOrderPromos(orderPromos.filter((p) => p.code !== bundleCode));
      setLinePromos(dropLinePromo(bundleCode));
      dispatch(
        warning(`${bundleCode} removed - the bundle is no longer complete`)
      );
      return;
    }

    setOrderProducts(orderProducts.filter((value, i) => i !== index));
  };

  const handleAddCustomProduct = async (product) => {
    setOrderProducts((prev) => [
      ...prev,
      {
        ...product,
      },
    ]);
    setOpenCustomProductModal(false);
  };

  const handleInputChange = _.debounce((value) => {
    if (value && value.length >= 3) {
      dispatch(getUsers({ filter: value }));
    }
  }, 1000);

  const handleSelectCustomer = (option) => {
    if (option.value === 'Guest') setShowGuestEmail(true);
    else {
      setShowGuestEmail(false);
      dispatch(getLastOrder(option.value));
    }

    setOrderPromos([]);
  };

  const handleChangeProductQty = (e, index) => {
    const { value } = e.target;
    if (!value) return;

    const newData = [...orderProducts];
    newData[index].qty = Number(value);
    newData[index].subtotal = newData[index].qty * newData[index].unitPrice;

    let totalScheduledQty = 0;
    deliverySchedules.forEach((s) => {
      totalScheduledQty += _.sumBy(
        s.products.filter((sp) => sp.uuid === newData[index].uuid),
        'qty'
      );
    });
    if (newData[index].qty < totalScheduledQty) {
      dispatch(warning('Warning, order qty less than delivery qty'));
    }

    const { bundleCode, bundleMinQty } = newData[index];
    if (bundleMinQty && newData[index].qty < bundleMinQty) {
      dispatch(
        warning(
          `${bundleCode} needs at least ${bundleMinQty} of this item or the bundle will be rejected`
        )
      );
    }

    setOrderProducts(newData);
  };

  const handleChangeProductPrice = (e, index) => {
    const { value } = e.target;
    if (!value) return;
    const newData = [...orderProducts];
    newData[index].unitPrice = Number(parseFloat(value).toFixed(2));
    newData[index].subtotal = newData[index].qty * newData[index].unitPrice;
    setOrderProducts(newData);
  };
  const handleChangeComparePrice = (e, index) => {
    const { value } = e.target;
    if (!value) return;
    const newData = [...orderProducts];
    newData[index].comparePrice = Number(parseFloat(value).toFixed(2));
    setOrderProducts(newData);
  };

  const handleAddSchedule = async (value) => {
    setOpenScheduleModal(false);
    let availableDelivery = availableDeliverySlots.find(
      (s) => s.id === value.id
    );
    if (!availableDelivery) {
      const fallback = await getAvailableDeliverySlotByDate(null, value.id);
      availableDeliverySlots.push(fallback);
    }
    availableDelivery = availableDeliverySlots.find((s) => s.id === value.id);
    const slot = availableDelivery.slots.find((s) => s.time === value.time);
    slot.count += 1;
    const newAvailableDeliverySlots = [...availableDeliverySlots];
    setAvailableDeliverySlots(newAvailableDeliverySlots);
    deliverySchedules.push(value);
    setDeliverySchedules([...deliverySchedules]);
  };

  const handleUpdateSchedule = (value) => {
    setOpenScheduleModal(false);
    const updatedSchedules = deliverySchedules.map((d) => {
      if (d.uuid === value.uuid) return value;
      return d;
    });
    setDeliverySchedules(updatedSchedules);
  };

  const handleRemoveSchedule = (index, delivery) => {
    setDeliverySchedules(deliverySchedules.filter((_value, i) => i !== index));
    const availableDelivery = availableDeliverySlots.find(
      (s) => s.id === delivery.id
    );
    if (availableDelivery) {
      const slot = availableDelivery.slots.find(
        (s) => s.time === delivery.time
      );
      if (slot) slot.count -= 1;
      setAvailableDeliverySlots([...availableDeliverySlots]);
    }
    setOpenScheduleModal(false);
  };

  const handleAddPayment = (value) => {
    setOpenPaymentModal(false);
    setPayments((prev) => [
      ...prev,
      {
        ...value,
      },
    ]);
  };
  const handleRemovePayment = (index) => {
    setPayments(payments.filter((_value, i) => i !== index));
    setOpenPaymentModal(false);
  };
  const handleUpdatePayment = (value, index) => {
    setOpenPaymentModal(false);
    const newPayments = [...payments];
    newPayments[index] = value;
    setPayments(newPayments);
  };

  const handlePrintDO = (delivery) => {
    dispatch(downloadDO(orderState.refNum, id, delivery.createdUnix));
  };

  const handleSendDO = (delivery) => {
    dispatch(sendDO(id, delivery.createdUnix));
  };

  const handleDownloadInvoiceVersion = (version) => {
    dispatch(downloadInvoiceVersion(id, version));
  };

  const handleCreateOrderComment = () => {
    if (!orderComment) return;
    const log = {
      uuid: uuidv4(),
      type: 'comment',
      comment: orderComment,
      createdOn: moment().toDate(),
      name: currentAdmin.name,
    };
    dispatch(createOrderLog(id, log));
    setOrderComment('');
  };

  const handleDeleteOrderLog = (logId, uuid) => {
    dispatch(deleteOrderLog(logId, uuid));
  };

  const handleCopyPaymentLink = () => {
    navigator.clipboard.writeText(paymentLinkData);
  };

  const renderPaymentInfo = (payment) => {
    switch (payment.type) {
      case PaymentTypes.ATOME:
        return <AtomePaymentInfo payment={payment} />;
      case PaymentTypes.HITPAY:
        return <HitPayPaymentInfo payment={payment} />;
      default:
        return <StripePaymentInfo payment={payment} />;
    }
  };

  const renderTaxInfo = () => {
    const tax = orderData.tax || calculateTax(orderData.finalPrice);
    return (
      <tr>
        <td>
          <small>GST ({(tax.rate * 100).toFixed(0)}%) Inclusive:</small>
        </td>
        <td>
          <small>SGD {tax.amount.toFixed(2)}</small>
        </td>
      </tr>
    );
  };

  return (
    orderState && (
      <MainStage
        title={!id ? 'New' : 'Update'}
        subtitle="Products"
        to="/orders"
      >
        <PageLoader loading={pageLoading} />
        <Row form>
          <Col md="12">
            <FormCard
              title={`Order details${id ? ` #${orderState.refNum}` : ''}`}
              actions={orderActions}
              onAction={handleOrderAction}
            >
              <Row>
                <Col md="4">
                  <h5>General</h5>
                  <Row>
                    {orderState.wpId && (
                      <Col>
                        <label>Old Reference</label>
                        <h6>#{orderState.wpId}</h6>
                      </Col>
                    )}
                    {id ? (
                      <Col>
                        <div className="mb-2">
                          <div>
                            <label>Customer</label>
                          </div>
                          <Link
                            to={`/customers/${orderState.userId}`}
                            target="_blank"
                          >
                            {orderState.user.firstName}{' '}
                            {orderState.user.lastName}
                          </Link>
                        </div>
                      </Col>
                    ) : (
                      <FormGroup
                        col={{ lg: '12' }}
                        type="searchable-select"
                        name="customer"
                        label="Customer"
                        placeholder="Type at least 3 characters"
                        onInputChange={handleInputChange}
                        onChange={handleSelectCustomer}
                        options={customerOptions}
                        control={control}
                        rules={{ required: 'Required field' }}
                        errors={errors}
                      />
                    )}

                    {showGuestEmail && (
                      <>
                        <FormGroup
                          col={{ lg: '12' }}
                          name="guestEmail"
                          label="Guest Email"
                          control={control}
                          rules={{ required: 'Required field' }}
                          errors={errors}
                        />
                        <FormGroup
                          col={{ lg: '6' }}
                          name="guestFirstName"
                          label="Guest First Name"
                          control={control}
                          rules={{ required: 'Required field' }}
                          errors={errors}
                        />
                        <FormGroup
                          col={{ lg: '6' }}
                          name="guestLastName"
                          label="Guest Last Name"
                          control={control}
                          rules={{ required: 'Required field' }}
                          errors={errors}
                        />
                      </>
                    )}

                    <FormGroup
                      col={{ lg: '12' }}
                      type="datepicker"
                      name="orderedOn"
                      label="Order Date"
                      control={control}
                      rules={{ required: 'Required field' }}
                      errors={errors}
                      readOnly={!!id}
                      defaultValue={orderState.orderedOn}
                    />

                    <FormGroup
                      col={{ lg: '12' }}
                      type="select"
                      options={orderStatusOptions}
                      name="status"
                      label="Status"
                      control={control}
                      rules={{ required: 'Required field' }}
                      errors={errors}
                      defaultValue={orderState.status}
                    />

                    <FormGroup
                      col={{ lg: '12' }}
                      name="referral.code"
                      label="Referral Code"
                      control={control}
                      readOnly={!!id}
                      defaultValue={
                        orderState.referral ? orderState.referral.code : ''
                      }
                    />

                    <FormGroup
                      type="textarea"
                      name="remarks"
                      col={{ lg: '12' }}
                      label="Remarks"
                      control={control}
                      defaultValue={orderState.remarks}
                    />

                    {paymentLinkData && (
                      <Col lg={12}>
                        <label>Payment link</label>
                        <InputGroup>
                          <FormInput
                            ref={paymentLinkRef}
                            readOnly
                            value={paymentLinkData}
                          />
                          <InputGroupAddon type="append">
                            <Button
                              id="btnClipboard"
                              theme="primary"
                              type="button"
                              onClick={() => handleCopyPaymentLink()}
                            >
                              <MaterialIcon icon="content_copy" />
                            </Button>
                            <Tooltip
                              target="#btnClipboard"
                              open={tooltipClipboard}
                              toggle={() => {
                                setTooltipClipboard(!tooltipClipboard);
                              }}
                            >
                              Copy url to clipboard
                            </Tooltip>
                          </InputGroupAddon>
                        </InputGroup>
                      </Col>
                    )}
                  </Row>
                </Col>
                <Col md="4">
                  <h5>Billing</h5>
                  <Row>
                    <FormGroup
                      name="billing.firstName"
                      col={{ lg: '6' }}
                      label="First Name"
                      control={control}
                      defaultValue={orderState.billing.firstName}
                    />
                    <FormGroup
                      name="billing.lastName"
                      col={{ lg: '6' }}
                      label="Last Name"
                      control={control}
                      defaultValue={orderState.billing.lastName}
                    />
                    <FormGroup
                      type="email"
                      name="billing.email"
                      col={{ lg: '12' }}
                      label="Email"
                      control={control}
                      errors={errors}
                      defaultValue={orderState.billing.email}
                    />

                    <FormGroup
                      name="billing.company"
                      col={{ lg: '6' }}
                      label="Company"
                      control={control}
                      defaultValue={orderState.billing.company}
                    />
                    <FormGroup
                      name="billing.contactNo"
                      col={{ lg: '6' }}
                      label="Contact"
                      control={control}
                      defaultValue={orderState.billing.contactNo}
                    />
                    <FormGroup
                      name="billing.postalCode"
                      col={{ lg: '6' }}
                      label="Postal"
                      control={control}
                      defaultValue={orderState.billing.postalCode}
                    />
                    <Col lg="6" className="form-group d-flex align-items-end">
                      <Button
                        type="button"
                        theme="secondary"
                        className="d-flex align-items-center"
                        disabled={lookingUpAddress.billing}
                        onClick={() => handleAddressLookup('billing')}
                      >
                        <MaterialIcon className="mr-2" icon="search" />
                        Look up
                      </Button>
                    </Col>
                    <FormGroup
                      name="billing.address"
                      col={{ lg: '12' }}
                      label="Address"
                      control={control}
                      defaultValue={orderState.billing.address}
                      onChange={() => cancelAddressLookup('billing')}
                    />
                    <FormGroup
                      type="select"
                      options={shippingCountriesOptions}
                      name="billing.country"
                      label="Country"
                      control={control}
                      defaultValue={orderState.billing.country}
                    />
                  </Row>
                </Col>
                <Col md="4">
                  <h5>
                    Shipping
                    <small className="d-inline-block ml-3">
                      <FormCheckbox
                        className="m-0"
                        checked={copyAddress}
                        onChange={() => setCopyAddress(!copyAddress)}
                      >
                        same as billing address
                      </FormCheckbox>
                    </small>
                  </h5>
                  <Row>
                    <FormGroup
                      name="shipping.firstName"
                      col={{ lg: '6' }}
                      label="First Name"
                      control={control}
                      defaultValue={orderState.shipping.firstName}
                    />
                    <FormGroup
                      name="shipping.lastName"
                      col={{ lg: '6' }}
                      label="Last Name"
                      control={control}
                      defaultValue={orderState.shipping.lastName}
                    />
                    <FormGroup
                      type="email"
                      name="shipping.email"
                      col={{ lg: '12' }}
                      label="Email"
                      control={control}
                      errors={errors}
                      defaultValue={orderState.shipping.email}
                    />
                    <FormGroup
                      name="shipping.company"
                      col={{ lg: '6' }}
                      label="Company"
                      control={control}
                      defaultValue={orderState.shipping.company}
                    />
                    <FormGroup
                      name="shipping.contactNo"
                      col={{ lg: '6' }}
                      label="Contact"
                      control={control}
                      defaultValue={orderState.shipping.contactNo}
                    />
                    <FormGroup
                      name="shipping.postalCode"
                      col={{ lg: '6' }}
                      label="Postal"
                      control={control}
                      defaultValue={orderState.shipping.postalCode}
                    />
                    <Col lg="6" className="form-group d-flex align-items-end">
                      <Button
                        type="button"
                        theme="secondary"
                        className="d-flex align-items-center"
                        disabled={lookingUpAddress.shipping}
                        onClick={() => handleAddressLookup('shipping')}
                      >
                        <MaterialIcon className="mr-2" icon="search" />
                        Look up
                      </Button>
                    </Col>
                    <FormGroup
                      name="shipping.address"
                      col={{ lg: '12' }}
                      label="Address"
                      control={control}
                      defaultValue={orderState.shipping.address}
                      onChange={() => cancelAddressLookup('shipping')}
                    />
                    <FormGroup
                      type="select"
                      options={shippingCountriesOptions}
                      name="shipping.country"
                      label="Country"
                      control={control}
                      defaultValue={orderState.shipping.country}
                    />
                    <FormGroup
                      type="textarea"
                      name="shipping.notes"
                      col={{ lg: '12' }}
                      label="Notes"
                      control={control}
                      defaultValue={orderState.shipping.notes}
                    />
                    <FormGroup
                      type="number"
                      name="shippingFee"
                      col={{ lg: '6' }}
                      label="Shipping Fee (SGD)"
                      control={control}
                      defaultValue={orderState.shippingFee}
                    />
                  </Row>
                </Col>
              </Row>
            </FormCard>
          </Col>

          <Col lg="9">
            <Card className="mb-4">
              <CardHeader className="border-bottom">
                <div className="d-flex align-items-center">
                  <h6 className="m-0 mr-auto">Products</h6>
                  <div className="d-flex">
                    <Button
                      theme="secondary"
                      type="button"
                      className="btn-sm mr-2"
                      disabled={isLoading}
                      onClick={() => {
                        setOpenCustomProductModal(!openCustomProductModal);
                      }}
                    >
                      Ad-hoc
                    </Button>
                    <Button
                      theme="secondary"
                      type="button"
                      className="btn-sm mr-2"
                      disabled={isLoading}
                      onClick={() => {
                        // the bundle's promo is validated per customer, so
                        // asking before the picker beats failing after it
                        if (!getSelectedUserId()) {
                          dispatch(
                            warning("Missing customer, can't add a bundle")
                          );
                          return;
                        }
                        setOpenAddBundleModal(!openAddBundleModal);
                      }}
                    >
                      Bundle
                    </Button>
                    <IconButton
                      className="p-2"
                      theme="success"
                      onClick={() => setOpenProductModal(!openProductModal)}
                      icon="add"
                    />
                  </div>
                </div>
              </CardHeader>
              <ListGroup flush>
                <ListGroupItem className="p-3">
                  <Row>
                    <Col>
                      {orderProducts.length > 0 && (
                        <div className="table-responsive">
                          <ProductTable className="table form-table">
                            <thead>
                              <tr>
                                <th>Item</th>
                                <th>Variant</th>
                                <th width="80">Quantity</th>
                                <th width="100">Unit Price</th>
                                <th width="100">Compare Price</th>
                                <th width="120">Total</th>
                                <th>-</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderProducts.map((product, index) => {
                                return (
                                  <tr key={index}>
                                    <ProductTd className="d-flex">
                                      <img src={product.image} alt="" />
                                      <div className="p-2">
                                        <div>
                                          {product.shippable &&
                                            product.status && (
                                              <small>
                                                <Badge
                                                  theme={
                                                    ProductStatusColor[
                                                      product.status
                                                        .replaceAll(' ', '_')
                                                        .replaceAll('-', '_')
                                                        .toUpperCase()
                                                    ]
                                                  }
                                                >
                                                  {product.status}
                                                </Badge>
                                                <span
                                                  className={
                                                    product.status ===
                                                    ProductStatus.PRE_ORDER
                                                      ? 'ml-2'
                                                      : 'd-none'
                                                  }
                                                >
                                                  {product.statusDate &&
                                                    moment(
                                                      product.statusDate
                                                    ).format('DD MMM YYYY')}
                                                  {product.statusNote || ''}
                                                </span>
                                                <br />
                                              </small>
                                            )}
                                          <div className="my-1">
                                            <strong>
                                              {product.collection}
                                            </strong>
                                          </div>
                                          {product.name}
                                          {product.subVariants &&
                                            product.subVariants.length > 0 &&
                                            product.subVariants
                                              .filter((sub) => sub.value)
                                              .map((sub, i) => {
                                                return (
                                                  <div key={`${sub}_${i}`}>
                                                    <small>+ {sub.value}</small>
                                                  </div>
                                                );
                                              })}
                                        </div>
                                        {product.bundleCode && (
                                          <LinePromoBadge
                                            outline
                                            theme={PromosColor.BUNDLE}
                                            className="mr-1"
                                          >
                                            {product.bundleCode} bundle
                                          </LinePromoBadge>
                                        )}
                                        {productPromos(product).map(
                                          (promo, pIndex) => {
                                            return (
                                              <LinePromoBadge
                                                key={pIndex}
                                                outline
                                                className="mr-1"
                                                theme={
                                                  PromosColor[
                                                    String(promo.type)
                                                      .replace(' ', '_')
                                                      .toUpperCase()
                                                  ] || 'secondary'
                                                }
                                              >
                                                {promo.code}
                                              </LinePromoBadge>
                                            );
                                          }
                                        )}
                                      </div>
                                    </ProductTd>
                                    <td>{product.variant}</td>
                                    <td>
                                      <FormGroup
                                        name={`products[${index}].qty`}
                                        control={control}
                                        rules={{ required: 'Required field' }}
                                        errors={errors}
                                        className="p-0"
                                        type="number"
                                        min={1}
                                        readOnly={!!product.promoCode}
                                        defaultValue={product.qty}
                                        onChange={(e) => {
                                          handleChangeProductQty(e, index);
                                          return 0;
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <FormGroup
                                        name={`products[${index}].unitPrice`}
                                        control={control}
                                        rules={{ required: 'Required field' }}
                                        errors={errors}
                                        className="p-0"
                                        type="number"
                                        min={1}
                                        defaultValue={product.unitPrice}
                                        onChange={(e) => {
                                          handleChangeProductPrice(e, index);
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <FormGroup
                                        name={`products[${index}].comparePrice`}
                                        control={control}
                                        rules={{ required: 'Required field' }}
                                        errors={errors}
                                        className="p-0"
                                        type="number"
                                        min={1}
                                        defaultValue={product.comparePrice}
                                        onChange={(e) => {
                                          handleChangeComparePrice(e, index);
                                        }}
                                      />
                                    </td>
                                    <td>
                                      {product.currency}{' '}
                                      {product.subtotal.toFixed(2)}
                                    </td>
                                    <td>
                                      {!product.promoCode && (
                                        <DialogButton
                                          className="p-1"
                                          theme="danger"
                                          icon="delete_outline"
                                          message={removeProductMessage(
                                            product
                                          )}
                                          onConfirm={() => {
                                            handleRemoveProduct(index);
                                          }}
                                          disabled={isLoading}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </ProductTable>
                        </div>
                      )}

                      {id && (
                        <>
                          <hr />
                          <SummaryTable>
                            <tbody>
                              <tr>
                                <td width="110">Sub Total: </td>
                                <td width="110">
                                  SGD {orderData.totalPrice.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <td>Discount: </td>
                                <td className="text-danger">
                                  SGD {orderData.discount.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <td>Shipping: </td>
                                <td>
                                  SGD{' '}
                                  {orderData.shippingFee
                                    ? orderData.shippingFee.toFixed(2)
                                    : '0'}
                                </td>
                              </tr>
                              <tr>
                                <td>Grand Total: </td>
                                <td>SGD {orderData.finalPrice.toFixed(2)}</td>
                              </tr>
                              {renderTaxInfo()}
                            </tbody>
                          </SummaryTable>
                        </>
                      )}
                    </Col>
                  </Row>
                </ListGroupItem>
              </ListGroup>
            </Card>

            <Card className="mb-4">
              <CardHeader className="border-bottom">
                <div className="d-flex align-items-center">
                  <h6 className="m-0 mr-auto">Payments</h6>
                  <IconButton
                    className="p-2"
                    theme="success"
                    onClick={() => {
                      setOpenPaymentModal(true);
                      setPaymentModalItemIndex(-1);
                    }}
                    icon="add"
                  />
                </div>
              </CardHeader>
              <ListGroup flush>
                <ListGroupItem className="p-3">
                  {payments.length > 0 && (
                    <Row>
                      <Col>
                        <div className="table-responsive">
                          <ProductTable className="table table-striped">
                            <thead>
                              <tr>
                                <th>Payment Date</th>
                                <th>Payment Type</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Remarks</th>
                                <th>-</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payments.map((p, index) => {
                                // For older payment, they might not have status, so we assume they are "success"
                                const paymentStatus = !p.status
                                  ? PaymentStatus.SUCCESS
                                  : p.status;

                                return (
                                  <tr key={index}>
                                    <td>
                                      {moment(p.paidOn).format(
                                        'D MMM YYYY h:mm a'
                                      )}
                                    </td>
                                    <td>{p.type}</td>
                                    <td>
                                      <Badge
                                        theme={
                                          PaymentStatusColors[
                                            _.snakeCase(
                                              paymentStatus
                                            ).toUpperCase()
                                          ]
                                        }
                                      >
                                        {_.startCase(paymentStatus)}
                                      </Badge>
                                    </td>
                                    <td>
                                      <span
                                        className={
                                          p.amount < 0 ||
                                          [
                                            PaymentStatus.PAYMENT_ERROR,
                                          ].includes(p.status)
                                            ? 'text-danger'
                                            : 'text-success'
                                        }
                                      >
                                        {p.currency}{' '}
                                        {p.amount
                                          ? p.amount.toFixed(2)
                                          : '0.00'}
                                      </span>
                                    </td>
                                    <td>{renderPaymentInfo(p)}</td>
                                    <td className="d-flex">
                                      <IconButton
                                        className="p-1 mr-2"
                                        theme="info"
                                        icon="edit"
                                        onClick={() => {
                                          setOpenPaymentModal(true);
                                          setPaymentModalItemIndex(index);
                                        }}
                                      />
                                      <DialogButton
                                        className="p-1"
                                        theme="danger"
                                        icon="delete_outline"
                                        message=""
                                        onConfirm={() => {
                                          handleRemovePayment(index);
                                        }}
                                        disabled={isLoading}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </ProductTable>
                        </div>
                      </Col>
                    </Row>
                  )}
                </ListGroupItem>
              </ListGroup>
            </Card>

            {orderProducts.length > 0 && (
              <Card className="mb-4">
                <CardHeader className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h6 className="m-0 mr-auto">Schedules</h6>
                    <IconButton
                      className="p-2"
                      theme="success"
                      onClick={() => setOpenScheduleModal(!openScheduleModal)}
                      icon="add"
                    />
                  </div>
                </CardHeader>
                <ListGroup flush>
                  <ListGroupItem className="p-3">
                    <FormCheckbox
                      toggle
                      small
                      className="mb-2"
                      checked={itemView}
                      onChange={() => setItemView(!itemView)}
                    >
                      Item View
                    </FormCheckbox>
                    <Row>
                      <Col>
                        {itemView && deliveryScheduleItems.length > 0 && (
                          <div className="table-responsive">
                            <ProductTable className="table table-striped">
                              <thead>
                                <tr>
                                  <th>Item</th>
                                  <th>Variant</th>
                                  <th>Delivery Date</th>
                                  <th>Delivery Time</th>
                                  <th>Delivery Status</th>
                                  <th>Stock Allocated</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deliveryScheduleItems.map((item, index) => {
                                  return (
                                    <tr key={index}>
                                      <td>{item.name}</td>
                                      <td>{item.variant}</td>
                                      <td>
                                        {item.date
                                          ? moment(item.date).format(
                                              'D MMM YYYY'
                                            )
                                          : '--'}
                                      </td>
                                      <td>{item.time ? item.time : '--'}</td>
                                      <td>
                                        {item.status ? (
                                          <Badge
                                            theme={
                                              StatusColors[
                                                item.status
                                                  .replace(' ', '_')
                                                  .toUpperCase()
                                              ]
                                            }
                                          >
                                            {item.status}
                                          </Badge>
                                        ) : (
                                          '--'
                                        )}
                                      </td>
                                      <td>
                                        <Badge
                                          theme={
                                            item.allocated
                                              ? 'success'
                                              : 'danger'
                                          }
                                        >
                                          <MaterialIcon
                                            icon={
                                              item.allocated ? 'done' : 'close'
                                            }
                                          />
                                        </Badge>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </ProductTable>
                          </div>
                        )}

                        {!itemView && deliverySchedules.length > 0 && (
                          <div className="table-responsive">
                            <ProductTable className="table table-striped">
                              <thead>
                                <tr>
                                  <th>Delivery Items</th>
                                  <th>Delivery Date</th>
                                  <th>Delivery Time</th>
                                  <th>Delivery Status</th>
                                  <th>-</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deliverySchedules.map((delivery, index) => {
                                  return (
                                    <tr key={index}>
                                      <td>
                                        {delivery.products.map((p, pIndex) => {
                                          return (
                                            <div key={pIndex}>
                                              <span className="mr-3">
                                                Qty: {p.qty}
                                              </span>
                                              {p.name} -- {p.variant}
                                            </div>
                                          );
                                        })}
                                      </td>
                                      <td>
                                        {moment(delivery.date).format(
                                          'D MMM YYYY'
                                        )}
                                      </td>
                                      <td>{delivery.time}</td>
                                      <td>
                                        {delivery.status && (
                                          <Badge
                                            theme={
                                              StatusColors[
                                                delivery.status
                                                  .replace(' ', '_')
                                                  .toUpperCase()
                                              ]
                                            }
                                          >
                                            {delivery.status}
                                          </Badge>
                                        )}
                                      </td>
                                      <td>
                                        <div className="d-flex">
                                          {delivery.uuid && (
                                            <IconButton
                                              disabled={isLoading}
                                              className="mr-2 p-1"
                                              theme="primary"
                                              onClick={() => {
                                                setSelectedDelivery(delivery);
                                                setOpenScheduleModal(
                                                  !openScheduleModal
                                                );
                                              }}
                                              icon="edit"
                                            />
                                          )}
                                          {delivery.printable && (
                                            <>
                                              <IconButton
                                                disabled={isLoading}
                                                className="mr-2 p-1"
                                                theme="primary"
                                                onClick={() => {
                                                  handlePrintDO(delivery);
                                                }}
                                                icon="print"
                                              />
                                              <DialogButton
                                                disabled={isLoading}
                                                className="mr-2 p-1"
                                                theme="success"
                                                icon="mail_outline"
                                                onConfirm={() => {
                                                  handleSendDO(delivery);
                                                }}
                                              />
                                            </>
                                          )}
                                          <DialogButton
                                            className="p-1"
                                            theme="danger"
                                            icon="delete_outline"
                                            onConfirm={() => {
                                              handleRemoveSchedule(
                                                index,
                                                delivery
                                              );
                                            }}
                                            disabled={isLoading}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </ProductTable>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </ListGroupItem>
                </ListGroup>
              </Card>
            )}
          </Col>
          <Col lg="3">
            <Card className="mb-4">
              <CardHeader className="border-bottom">
                <div className="d-flex align-items-center">
                  <h6 className="m-0 mr-auto">Redeems</h6>
                  <IconButton
                    className="p-2"
                    theme="success"
                    onClick={() => setOpenPromoModal(!openPromoModal)}
                    icon="add"
                  />
                </div>
              </CardHeader>
              <ListGroup flush>
                <ListGroupItem className="p-3">
                  <Row>
                    <Col>
                      <CommonList>
                        {orderPromos.map((promo, index) => {
                          return (
                            <li key={index}>
                              <Badge
                                theme={
                                  PromosColor[
                                    promo.type.replace(' ', '_').toUpperCase()
                                  ]
                                }
                              >
                                {promo.code}
                              </Badge>
                              <DialogButton
                                className="p-1"
                                theme="danger"
                                icon="delete_outline"
                                message=""
                                onConfirm={() => {
                                  handleRemovePromo(promo.code, index);
                                }}
                                disabled={isLoading}
                              />
                            </li>
                          );
                        })}
                      </CommonList>
                    </Col>
                  </Row>
                </ListGroupItem>
              </ListGroup>
            </Card>

            {orderReport && (
              <Card className="mb-4">
                <CardHeader className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h6 className="m-0 mr-auto">Invoice Version</h6>
                  </div>
                </CardHeader>
                <ListGroup flush>
                  <ListGroupItem className="p-3">
                    <Row>
                      <Col>
                        <CommonList>
                          {orderReport.invoices.map((invoice, index) => {
                            return (
                              <li key={index}>
                                {moment
                                  .unix(invoice.date.seconds)
                                  .format('DD MMM YYYY, hh:mm a')}
                                <IconButton
                                  disabled={isLoading}
                                  className="mr-2 p-1"
                                  theme="primary"
                                  onClick={() => {
                                    handleDownloadInvoiceVersion(
                                      invoice.version
                                    );
                                  }}
                                  icon="print"
                                />
                              </li>
                            );
                          })}
                        </CommonList>
                      </Col>
                    </Row>
                  </ListGroupItem>
                </ListGroup>
              </Card>
            )}

            {orderLog && orderLog.id === id && (
              <Card className="mb-4">
                <CardHeader className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h6 className="m-0 mr-auto">Activity Logs</h6>
                  </div>
                </CardHeader>
                <ListGroup flush>
                  <ListGroupItem className="p-0">
                    <div className="border-bottom d-block pt-2 px-2">
                      <Row>
                        <FormInputGroup
                          className="m-0"
                          col={{ lg: '12' }}
                          name="comment"
                          control={control}
                          value={orderComment}
                          placeholder="Type a comment"
                          onChange={(value) => setOrderComment(value)}
                          onClick={() => handleCreateOrderComment()}
                        />
                      </Row>
                    </div>
                    <ActivityList>
                      {orderLog.logs.map((log, index) => {
                        return (
                          <li className="border-bottom" key={index}>
                            <OrderLog
                              data={log}
                              onDelete={() => {
                                handleDeleteOrderLog(orderLog.id, log.uuid);
                              }}
                            />
                          </li>
                        );
                      })}
                    </ActivityList>
                  </ListGroupItem>
                </ListGroup>
              </Card>
            )}
          </Col>

          <Col lg={12} className="mt-4">
            <Button
              theme="primary"
              disabled={isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
            {id && (
              <Button
                theme="secondary"
                type="button"
                className="btn-sm ml-2"
                disabled={isLoading}
                onClick={() => setOpenPreviewModal(!openPreviewModal)}
              >
                Preview
              </Button>
            )}
          </Col>
        </Row>

        <ProductModal
          open={openProductModal}
          onClose={() => setOpenProductModal(false)}
          onAddProduct={handleAddProduct}
          products={productsData.items}
        />

        <AddBundleModal
          open={openAddBundleModal}
          onClose={() => setOpenAddBundleModal(false)}
          onConfirm={handleConfirmAddBundle}
          products={productsData.items}
        />

        <CustomProductModal
          open={openCustomProductModal}
          onClose={() => setOpenCustomProductModal(false)}
          onAddProduct={handleAddCustomProduct}
        />

        <PaymentModal
          open={openPaymentModal}
          onClose={() => setOpenPaymentModal(false)}
          onAddPayment={(value) => handleAddPayment(value)}
          onUpdatePayment={handleUpdatePayment}
          payments={payments}
          editIndex={paymentModalItemIndex}
        />

        {orderProducts.length > 0 && (
          <ScheduleModal
            open={openScheduleModal}
            delivery={selectedDelivery}
            products={orderProducts}
            deliverySlots={availableDeliverySlots}
            existingSchdules={deliverySchedules}
            onClose={() => {
              setSelectedDelivery(null);
              setOpenScheduleModal(false);
            }}
            onAddSchedule={(value) => handleAddSchedule(value)}
            onUpdateSchedule={(value) => handleUpdateSchedule(value)}
          />
        )}

        <PromoModal
          open={openPromoModal}
          onClose={() => setOpenPromoModal(false)}
          onApply={handleApplyOrderPromo}
        />

        <PreviewModal
          open={openPreviewModal}
          onClose={() => setOpenPreviewModal(false)}
          order={orderState}
        />

        {bundlePromotion && (
          <BundleModal
            open={openBundleModal}
            data={bundlePromotion}
            onClose={() => setOpenBundleModal(false)}
            onConfirm={handleConfirmBundle}
          />
        )}
      </MainStage>
    )
  );
};

export default ProductForm;
